import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import type {
  Filter,
  FilterDefinition,
  FilterMatchMode,
} from "@/shared/components/ResourceView";
import type { NumericAggregation, Period } from "@/shared/domain/metrics";
import { metricsQueryKeys } from "@/shared/domain/metrics/hooks";
import { hashMetricsQueryKey } from "@/shared/domain/metrics/hooks/metricsQueryKeys";
import type { Condition } from "@/types";

import {
  type BucketCountsSource,
  bucketCountsRequest,
  bucketValues,
  facetCondition,
} from "./bucketCounts";
import type { BucketWindow } from "./bucketWindow";

/** How long a bucket's counts stay fresh: as long as a chart's aggregate. */
const STALE_TIME_MS = 60_000;

/**
 * Counts a drilled bucket's values, a field at a time, for
 * `withBucketCounts`: the chart's aggregate over the bucket, under the
 * drill-down's filters that narrow the field (see `facetCondition`), split by
 * the field. Fetched when a field's values are first listed, and cached like
 * any aggregate.
 *
 * `undefined` where the window can't be counted: a raw card's, which has no
 * `aggregation` (see `bucketCountsRequest`), or nothing drilled.
 */
export function useBucketCounts({
  metricName,
  period,
  aggregation,
  bucket,
  orgId,
  deviceIds,
  sessionIds,
  condition,
  filters,
  matchMode,
  definitions,
}: {
  metricName: string;
  period: Period;
  aggregation: NumericAggregation | undefined;
  bucket: BucketWindow | null;
  orgId: string | undefined;
  deviceIds?: string[];
  sessionIds?: string[];
  /** The dashboard's filter condition. */
  condition?: Condition;
  /** The drill-down's own filters. */
  filters: readonly Filter[];
  matchMode: FilterMatchMode;
  definitions: FilterDefinition[] | undefined;
}): ((field: string) => BucketCountsSource) | undefined {
  const queryClient = useQueryClient();
  const services = useDomainServices();
  return React.useMemo(() => {
    if (aggregation === undefined || bucket === null || orgId === undefined) {
      return undefined;
    }
    return (field: string) => {
      const request = bucketCountsRequest({
        metricName,
        period,
        aggregation,
        bucket,
        deviceIds,
        sessionIds,
        condition,
        drillCondition: facetCondition(filters, field, {
          matchMode,
          definitions,
        }),
        field,
      });
      const queryKey = metricsQueryKeys.aggregate(request, orgId);
      return {
        key: hashMetricsQueryKey(queryKey),
        load: async () => {
          const response = await queryClient.fetchQuery({
            queryKey,
            // The bucket's bounds are nanosecond bigints.
            queryKeyHashFn: hashMetricsQueryKey,
            queryFn: ({ signal }) =>
              services.metricsService.aggregateMetric(request, {
                abortSignal: signal,
                orgId,
              }),
            staleTime: STALE_TIME_MS,
          });
          return bucketValues(response, bucket);
        },
      };
    };
  }, [
    queryClient,
    services,
    metricName,
    period,
    aggregation,
    bucket,
    orgId,
    deviceIds,
    sessionIds,
    condition,
    filters,
    matchMode,
    definitions,
  ]);
}
